import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Search, HeadphonesIcon, MessageSquare, Clock, 
  CheckCircle, AlertCircle, Send, Sparkles, ArrowLeft
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserSupportPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [formData, setFormData] = useState({ subject: '', message: '' });

  const fetchTickets = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/support`);
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Error al cargar tickets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/support`, formData);
      toast.success('Ticket creado exitosamente');
      setShowCreateModal(false);
      setFormData({ subject: '', message: '' });
      fetchTickets();
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      open: { bg: 'bg-amber-50', text: 'text-amber-600', icon: AlertCircle, label: t('open'), dot: 'bg-amber-400' },
      in_progress: { bg: 'bg-blue-50', text: 'text-blue-600', icon: Clock, label: t('inProgress'), dot: 'bg-blue-400' },
      resolved: { bg: 'bg-emerald-50', text: 'text-emerald-600', icon: CheckCircle, label: t('resolved'), dot: 'bg-emerald-400' },
      closed: { bg: 'bg-gray-100', text: 'text-gray-500', icon: CheckCircle, label: t('closed'), dot: 'bg-gray-400' },
    };
    return configs[status] || configs.open;
  };

  const filteredTickets = tickets.filter(ticket =>
    ticket.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Detect if user is client (mobile layout)
  const isClient = user?.user_type === 'client';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 rounded-full border-4 border-amber-100 border-t-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className={`space-y-5 animate-fade-in ${isClient ? 'max-w-2xl mx-auto' : ''}`} data-testid="user-support-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="pt-1">
          {isClient && (
            <button
              onClick={() => navigate('/client/help')}
              className="flex items-center gap-1.5 text-sm text-amber-500 font-semibold mb-3 hover:gap-2.5 transition-all duration-200"
              data-testid="back-to-help"
            >
              <ArrowLeft size={16} /> Volver a Ayuda
            </button>
          )}
          <h1 className={`font-bold text-gray-900 ${isClient ? 'text-3xl' : 'text-4xl text-[#467322]'}`} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Soporte
          </h1>
          <p className={`mt-1 font-medium ${isClient ? 'text-sm text-gray-400' : 'text-gray-600'}`}>Mis tickets de soporte</p>
        </div>
        <Button 
          onClick={() => setShowCreateModal(true)}
          className={isClient 
            ? "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-full px-5 shadow-lg shadow-amber-500/25 font-bold"
            : "bg-[#467322] hover:bg-[#3A601C] text-white"
          }
          data-testid="create-ticket-btn"
        >
          <Plus size={18} className="mr-1.5" />
          {t('newTicket')}
        </Button>
      </div>

      {/* Search */}
      {isClient ? (
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <Input
            placeholder={`${t('search')}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-11 bg-white border-gray-100 focus:border-amber-400 focus:ring-2 focus:ring-amber-500/20 rounded-xl h-11 shadow-sm"
          />
        </div>
      ) : (
        <div className="bg-white border border-[#467322]/20 rounded-lg p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#467322]" size={20} />
            <Input
              placeholder={`${t('search')}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-[#467322]/30 focus:border-[#467322]"
            />
          </div>
        </div>
      )}

      {/* Tickets List */}
      {filteredTickets.length === 0 ? (
        <div className={`${isClient ? 'bg-white rounded-3xl border border-gray-100/50 shadow-sm' : 'bg-white border border-[#467322]/20 rounded-lg'} p-10 text-center`}>
          {isClient ? (
            <>
              <div className="w-20 h-20 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <HeadphonesIcon size={32} className="text-amber-300" />
              </div>
              <p className="font-bold text-gray-700 text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{t('noTickets')}</p>
              <p className="text-sm text-gray-400 mt-1">Crea tu primer ticket de soporte</p>
              <Button 
                onClick={() => setShowCreateModal(true)}
                className="mt-5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-full px-6 shadow-lg shadow-amber-500/25 font-bold"
              >
                <Sparkles size={16} className="mr-1.5" />
                Crear mi primer ticket
              </Button>
            </>
          ) : (
            <>
              <HeadphonesIcon size={48} className="mx-auto mb-4 opacity-50" />
              <p>{t('noTickets')}</p>
              <Button onClick={() => setShowCreateModal(true)} className="mt-4 bg-[#467322] hover:bg-[#3A601C] text-white">
                Crear mi primer ticket
              </Button>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTickets.map((ticket, idx) => {
            const status = getStatusConfig(ticket.status);
            return isClient ? (
              <div 
                key={ticket.id}
                className="bg-white rounded-2xl border border-gray-100/50 overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
                style={{ animationDelay: `${idx * 60}ms` }}
                onClick={() => { setSelectedTicket(ticket); setShowDetailsModal(true); }}
              >
                <div className="h-1 bg-gradient-to-r from-amber-400 to-orange-500" />
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <HeadphonesIcon size={16} className="text-amber-500 flex-shrink-0" />
                        <span className="font-bold text-gray-900 text-sm truncate">{ticket.subject}</span>
                      </div>
                      <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">{ticket.message}</p>
                      <div className="flex items-center gap-3 mt-3">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${status.bg} ${status.text}`}>
                          <div className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                          {status.label}
                        </span>
                        <span className="text-[10px] text-gray-400 font-medium">
                          {new Date(ticket.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                        </span>
                        {ticket.response && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">
                            <MessageSquare size={9} /> Respondido
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div 
                key={ticket.id}
                className="bg-white border border-[#467322]/20 rounded-lg p-4 hover:shadow-md transition-all cursor-pointer"
                onClick={() => { setSelectedTicket(ticket); setShowDetailsModal(true); }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <HeadphonesIcon size={18} className="text-[#467322]" />
                      <span className="font-semibold text-black">{ticket.subject}</span>
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${status.bg} ${status.text}`}>
                        <status.icon size={12} />{status.label}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2">{ticket.message}</p>
                    <p className="text-xs text-gray-400 mt-2">{new Date(ticket.created_at).toLocaleString()}</p>
                  </div>
                  {ticket.response && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-[#467322]/10 text-[#467322] border border-[#467322]/20">
                      <MessageSquare size={12} /> Respondido
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className={`max-w-md ${isClient ? 'bg-white border-0 rounded-3xl' : 'bg-white border border-[#467322]/20'}`}>
          <DialogHeader>
            <DialogTitle className={`text-2xl ${isClient ? 'text-gray-900' : 'text-[#467322]'}`} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {t('newTicket')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-700 font-semibold text-sm">{t('subject')} *</Label>
              <Input
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                required
                placeholder="¿En qué podemos ayudarte?"
                className={isClient ? "bg-gray-50 border-transparent focus:bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-500/20 rounded-xl" : "border-[#467322]/30 focus:border-[#467322]"}
                data-testid="input-ticket-subject"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 font-semibold text-sm">{t('message')} *</Label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                rows={5}
                placeholder="Describe tu problema o consulta en detalle..."
                className={isClient ? "bg-gray-50 border-transparent focus:bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-500/20 rounded-xl" : "border-[#467322]/30 focus:border-[#467322]"}
                data-testid="input-ticket-message"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}
                className={isClient ? "border-gray-200 text-gray-600 rounded-full" : "border-[#467322] text-[#467322]"}
              >
                {t('cancel')}
              </Button>
              <Button type="submit"
                className={isClient 
                  ? "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-full font-bold shadow-lg shadow-amber-500/25"
                  : "bg-[#467322] hover:bg-[#3A601C] text-white"
                }
                data-testid="send-ticket-btn"
              >
                <Send size={16} className="mr-1.5" />
                Enviar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className={`max-w-lg ${isClient ? 'bg-white border-0 rounded-3xl' : 'bg-white border border-[#467322]/20'}`}>
          <DialogHeader>
            <DialogTitle className={`text-2xl ${isClient ? 'text-gray-900' : 'text-[#467322]'}`} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Ticket de Soporte
            </DialogTitle>
          </DialogHeader>
          
          {selectedTicket && (
            <div className="space-y-4">
              <div className={`p-4 rounded-xl ${isClient ? 'bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100/50' : 'bg-[#F2F7F0]'}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`font-bold ${isClient ? 'text-gray-900' : 'text-[#467322]'}`}>{selectedTicket.subject}</span>
                  {(() => {
                    const s = getStatusConfig(selectedTicket.status);
                    return (
                      <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${s.bg} ${s.text}`}>
                        <s.icon size={12} />{s.label}
                      </span>
                    );
                  })()}
                </div>
                <p className="text-xs text-gray-500">{new Date(selectedTicket.created_at).toLocaleString()}</p>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold text-sm">Mi Mensaje</Label>
                <div className={`p-4 rounded-xl ${isClient ? 'bg-gray-50 border border-gray-100' : 'bg-white border border-[#467322]/20'}`}>
                  <p className="text-gray-700 text-sm whitespace-pre-wrap">{selectedTicket.message}</p>
                </div>
              </div>

              {selectedTicket.response ? (
                <div className="space-y-2">
                  <Label className="text-gray-700 font-semibold text-sm flex items-center gap-2">
                    <MessageSquare size={14} className={isClient ? 'text-emerald-500' : 'text-[#467322]'} />
                    Respuesta del Soporte
                  </Label>
                  <div className={`p-4 rounded-xl ${isClient ? 'bg-emerald-50 border border-emerald-100/50' : 'bg-[#467322]/10 border border-[#467322]/20'}`}>
                    <p className="text-gray-700 text-sm whitespace-pre-wrap">{selectedTicket.response}</p>
                  </div>
                </div>
              ) : (
                <div className={`p-5 rounded-xl text-center ${isClient ? 'bg-amber-50 border border-amber-100/50' : 'bg-amber-50 border border-amber-200'}`}>
                  <Clock className="mx-auto mb-2 text-amber-500" size={24} />
                  <p className="text-amber-700 text-sm font-medium">Esperando respuesta del equipo de soporte</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setShowDetailsModal(false)}
              className={isClient 
                ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full font-bold"
                : "bg-[#467322] hover:bg-[#3A601C] text-white"
              }
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserSupportPage;
